import { useState, useEffect } from 'react';
import {
    Badge,
    IconButton,
    Menu,
    MenuItem,
    Typography,
    Box,
    Button,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import { useNotificationSync } from '../../hooks/useNotificationSync';

/**
 * Notification Bell Component - Shows notification count and dropdown menu
 */
const NotificationBell = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [lastFetchedCount, setLastFetchedCount] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { markAsReadSync, markAllAsReadSync, setupNotificationListener } = useNotificationSync();

    const open = Boolean(anchorEl);

    // Fetch unread count on component mount
    useEffect(() => {
        fetchUnreadCount();
        // Set up polling for unread count (every 30 seconds)
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(interval);
    }, []);

    // Listen for push notifications to update count in real-time
    useEffect(() => {
        const handleServiceWorkerMessage = (event) => {
            if (event.data && event.data.type === 'NEW_NOTIFICATION') {
                // When a push notification is received, refresh the unread count
                fetchUnreadCount();
                // If dropdown is open, refresh notifications too
                if (open) {
                    fetchNotifications();
                }
            }
        };

        // Listen for service worker messages
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
            
            return () => {
                navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
            };
        }
    }, [open]);

    // Set up listener for notification updates from other components
    useEffect(() => {
        const cleanup = setupNotificationListener(({ eventType, data }) => {
            switch (eventType) {
                case 'markAsRead':
                    // Remove from local state since we only show unread notifications
                    setNotifications(prev => prev.filter(notif => notif.id !== data.notificationId));
                    setUnreadCount(prev => Math.max(0, prev - 1));
                    break;
                case 'markAllAsRead':
                    // Clear all notifications since we only show unread ones
                    setNotifications([]);
                    setUnreadCount(0);
                    break;
                case 'dismiss':
                    // Remove from local state
                    setNotifications(prev => prev.filter(notif => notif.id !== data.notificationId));
                    // Update unread count (dismissed notifications might be unread)
                    setUnreadCount(prev => Math.max(0, prev - 1));
                    break;
                case 'newNotification':
                    // New notification received - refresh count
                    fetchUnreadCount();
                    // If dropdown is open, refresh notifications too
                    if (open) {
                        fetchNotifications();
                    }
                    break;
                default:
                    break;
            }
        });

        return cleanup;
    }, [setupNotificationListener]);

    const fetchUnreadCount = async () => {
        try {
            const response = await api.get('/api/notifications/unread-count');
            setUnreadCount(response.data.unreadCount);
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    };

    const fetchNotifications = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get('/api/notifications?limit=10&unread_only=true');
            setNotifications(response.data.notifications);
            setLastFetchedCount(unreadCount);
        } catch (error) {
            console.error('Error fetching notifications:', error);
            setError('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        const shouldFetch = 
            notifications.length === 0 || 
            unreadCount === 0 || 
            lastFetchedCount !== unreadCount;
        if (shouldFetch) {
            fetchNotifications();
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const markAsRead = async (notificationId) => {
        const success = await markAsReadSync(notificationId, api);
        if (success) {
            // Local state will be updated via the event listener
            // This ensures consistency with other components
        }
    };

    const markAllAsRead = async () => {
        const success = await markAllAsReadSync(api);
        if (success) {
            // Local state will be updated via the event listener
            // This ensures consistency with other components
        }
    };



    const getNotificationIcon = (type) => {
        switch (type) {
            case 'error': return '🚨';
            case 'warning': return '⚠️';
            case 'success': return '✅';
            case 'info':
            default: return 'ℹ️';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'urgent': return 'error';
            case 'high': return 'warning';
            case 'normal': return 'primary';
            case 'low': return 'default';
            default: return 'default';
        }
    };

    return (
        <>
            <IconButton 
                color="inherit" 
                onClick={handleClick}
                sx={{ mr: 1 }}
                aria-label={`${unreadCount} unread notifications`}
            >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                    {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
                </Badge>
            </IconButton>
            
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: {
                            height: notifications.length > 0 ? 450 : 250, // Smaller when no notifications
                            width: 400,
                            padding: 0,
                            display: 'flex',
                            flexDirection: 'column',
                        },
                    },
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                MenuListProps={{
                    sx: {
                        padding: 0,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }
                }}
            >
                {/* Fixed Header */}
                <Box sx={{ 
                    flexShrink: 0,
                    backgroundColor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    p: 2,
                    pb: 1
                }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            Notifications
                        </Typography>
                        {unreadCount > 0 && (
                            <Button
                                size="small"
                                startIcon={<MarkEmailReadIcon />}
                                onClick={markAllAsRead}
                            >
                                Mark all read
                            </Button>
                        )}
                    </Box>
                </Box>
                
                {/* Scrollable Content */}
                <Box sx={{ 
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0
                }}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : error ? (
                        <Box p={2}>
                            <Alert severity="error" size="small">
                                {error}
                            </Alert>
                        </Box>
                    ) : notifications.length === 0 ? (
                        <Box p={3} textAlign="center">
                            <Typography variant="body2" color="text.secondary">
                                No Unread Notifications
                            </Typography>
                        </Box>
                    ) : (
                        notifications.map((notification) => (
                            <MenuItem
                                key={notification.id}
                                sx={{
                                    whiteSpace: 'normal',
                                    alignItems: 'flex-start',
                                    py: 1.5,
                                    backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
                                    '&:hover': {
                                        backgroundColor: 'action.selected',
                                    }
                                }}
                                onClick={() => {
                                    handleClose();
                                    navigate('/profile/notifications');
                                }}
                            >
                                <Box sx={{ width: '100%' }}>
                                    <Box display="flex" alignItems="flex-start" gap={1}>
                                        <Typography sx={{ fontSize: '1.2em', mt: 0.5 }}>
                                            {getNotificationIcon(notification.type)}
                                        </Typography>
                                        
                                        <Box flex={1}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                <Typography 
                                                    variant="subtitle2" 
                                                    sx={{ 
                                                        fontWeight: notification.is_read ? 'normal' : 'bold',
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {notification.title}
                                                </Typography>
                                                
                                                <Box display="flex" gap={0.5} alignItems="center">
                                                    {notification.priority !== 'normal' && (
                                                        <Chip
                                                            label={notification.priority}
                                                            size="small"
                                                            color={getPriorityColor(notification.priority)}
                                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                                        />
                                                    )}
                                                    
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            markAsRead(notification.id);
                                                        }}
                                                        sx={{ p: 0.25 }}
                                                        title="Mark as read"
                                                    >
                                                        <MarkEmailReadIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                            
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary"
                                                sx={{ mb: 1 }}
                                            >
                                                {notification.message}
                                            </Typography>
                                            
                                            <Typography 
                                                variant="caption" 
                                                color="text.secondary"
                                            >
                                                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </MenuItem>
                        ))
                    )}
                </Box>
                
                {/* Fixed Footer */}
                <Box sx={{ 
                    flexShrink: 0,
                    backgroundColor: 'background.paper',
                    borderTop: '1px solid',
                    borderColor: 'divider'
                }}>
                    <Box p={1}>
                        <Button 
                            fullWidth 
                            size="small"
                            onClick={() => {
                                handleClose();
                                navigate('/profile/notifications');
                            }}
                        >
                            Manage Notifications
                        </Button>
                    </Box>
                </Box>
            </Menu>
        </>
    );
};

export default NotificationBell;
